import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ActivityCard } from './ActivityCard';
import type { IListItem } from '../model/listItem';
import { promiseFacts, zoneFloors } from '../WorkPlan/workPlanModel';
import type { IUseReadStateResult } from '@hooks/useReadState';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useWindowedList } from '@hooks/useWindowedList';
import styles from './cardList.module.css';

export interface ICardListProps {
  items: IListItem[];
  onOpen: (item: IListItem) => void;
  readState: Pick<IUseReadStateResult, 'isUnseen' | 'unreadComments'>;
  /** Key of the item whose drawer is open — its card never shows an unread
   *  indicator, since the user is actively reading it (avoids a one-frame flash
   *  when a comment is sent before the read count is persisted). */
  openKey?: string;
  /** While the read state is loading, suppress all indicators — otherwise every
   *  item looks unseen (absent from an empty map) and all cards flash a dot. */
  loading?: boolean;
  /** The viewer holds `issues.manage` — handed to each card. */
  canManageIssues: boolean;
  /** A signature of what the list SHOWS (filters, search, date window,
   *  schedule, alert drill-down): when it changes, the window returns to the
   *  first page. Not the `items` array — every live refetch mints a new array
   *  for the same view, and keying on it snapped a scrolled list back to the
   *  first 20 cards every few seconds. */
  resetKey: string;
}

export function CardList({ items, onOpen, readState, openKey, loading = false, canManageIssues, resetKey }: ICardListProps) {
  const { t } = useTranslation();
  // The site's floor catalog names the floor an activity's facts line leads
  // with — the Work Plan's bar says the same (`promiseFacts`).
  const { siteId = '' } = useParams<{ siteId: string }>();
  const { floors } = useSiteLocations(siteId);
  const floorOfZone = useMemo(() => zoneFloors(floors), [floors]);
  const { visibleCount, hasMore, sentinelRef } = useWindowedList(items.length, resetKey);

  if (items.length === 0) {
    return <div className={styles.empty}>{t('activities.empty')}</div>;
  }

  return (
    <ul className={styles.list}>
      {items.slice(0, visibleCount).map((i) => {
        const suppressed = loading || i.key === openKey;
        const task = i.raw.activity;
        return (
          <li key={i.key}>
            <ActivityCard
              item={i}
              onOpen={onOpen}
              unseen={suppressed ? false : readState.isUnseen(i.key)}
              unreadCount={suppressed ? 0 : readState.unreadComments(i.key, i.commentCount)}
              facts={task && promiseFacts(task, floors, floorOfZone)}
              canManageIssues={canManageIssues}
            />
          </li>
        );
      })}
      {hasMore && <li ref={sentinelRef} className={styles.sentinel} aria-hidden />}
    </ul>
  );
}
